import React, { useState, useEffect, useRef } from "react";
import { XIcon, UploadIcon, ImageIcon, CameraIcon } from "./icons/FeatherIcons";
import { ActivityCategory } from "../types";
import {
  getActivityImageOptions,
  preloadImage,
} from "../services/imageService";
import {
  processPhoto,
  createPreviewUrl,
  cleanupPreviewUrl,
} from "../services/photoUploadService";
import EnhancedPhotoSelectionModal from "./EnhancedPhotoSelectionModal";
import { crashlytics } from "../services/firebase";
import { fallbackImage } from "../utils/getCachedImage";

interface ActivityImageSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imageUrl: string) => void;
  category: ActivityCategory;
  title?: string;
  description?: string;
  location?: string;
  currentImageUrl?: string;
}

type TabType = "curated" | "upload";

const ActivityImageSelectionModal: React.FC<
  ActivityImageSelectionModalProps
> = ({
  isOpen,
  onClose,
  onSelect,
  category,
  title,
  description,
  location,
  currentImageUrl,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("curated");
  const [imageOptions, setImageOptions] = useState<string[]>([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>(
    currentImageUrl || "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  // Upload-related state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string>("");
  const [uploadError, setUploadError] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showEnhancedModal, setShowEnhancedModal] = useState(false);

  // Fetch image options when modal opens
  useEffect(() => {
    if (isOpen && imageOptions.length === 0) {
      setIsLoading(true);
      getActivityImageOptions(category, title, description, location, 12)
        .then(async (options) => {
          // 1. Preload all images and get the "final" URLs (either original or fallback)
          const validatedUrls = await Promise.all(
            options.map((url) => preloadImage(url, false)),
          );

          // out of 12 options take only first 9
          validatedUrls.splice(9);

          // 2. Filter out any nulls (failed to load and no fallback)
          const filteredUrls = validatedUrls.filter(
            (url): url is string => url !== null,
          );
          const fallbackUrl = await preloadImage("non-existent-url", true);
          if (fallbackUrl) {
            filteredUrls.push(fallbackUrl);
          }
          setImageOptions(filteredUrls);

          // 3. Update loaded set so UI knows they are ready
          const loadedSet = new Set<string>();
          filteredUrls.forEach((url) => loadedSet.add(url));
          setLoadedImages(loadedSet);
        })
        .catch((error) => {
          setImageOptions([]);
          crashlytics.logError(
            `Failed to fetch image options. Error: ${JSON.stringify(error?.message)}`,
            "ActivityImageSelectionModal.tsx",
            error,
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, category, title, description, location, imageOptions.length]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setImageOptions([]);
      setLoadedImages(new Set());
      setSelectedImageUrl(currentImageUrl || "");
      setActiveTab("curated");

      // Clean up upload state
      if (uploadPreviewUrl) {
        cleanupPreviewUrl(uploadPreviewUrl);
      }
      setUploadedFile(null);
      setUploadPreviewUrl("");
      setUploadError("");
      setIsProcessing(false);
      setShowEnhancedModal(false);
    }
  }, [isOpen, currentImageUrl, uploadPreviewUrl]);

  const handleImageLoad = (url: string) => {
    setLoadedImages((prev) => new Set(prev).add(url));
  };

  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    if (e.currentTarget.src !== fallbackImage) {
      e.currentTarget.src = fallbackImage;
    }
  };

  const handleSelectImage = (url: string) => {
    setSelectedImageUrl(url);
    // Clear upload selection when selecting curated image
    if (uploadPreviewUrl && url !== uploadPreviewUrl) {
      setUploadedFile(null);
      cleanupPreviewUrl(uploadPreviewUrl);
      setUploadPreviewUrl("");
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      setUploadError("");
      setUploadedFile(file);

      try {
        // Create preview URL
        const previewUrl = createPreviewUrl(file);
        setUploadPreviewUrl(previewUrl);
        setSelectedImageUrl(previewUrl);

        // Switch to upload tab
        setActiveTab("upload");
      } catch (error) {
        setUploadError("Failed to load image preview");
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleEnhancedPhotoSelect = async (dataUrl: string) => {
    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Create a file from the blob
      const file = new File([blob], `activity-photo-${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      setUploadError("");
      setUploadedFile(file);
      setUploadPreviewUrl(dataUrl);
      setSelectedImageUrl(dataUrl);

      // Switch to upload tab to show the selected image
      setActiveTab("upload");
      setShowEnhancedModal(false);
    } catch (error) {
      setUploadError("Failed to process selected photo");
    }
  };

  const handleRemoveUpload = () => {
    if (uploadPreviewUrl) {
      cleanupPreviewUrl(uploadPreviewUrl);
    }
    setUploadedFile(null);
    setUploadPreviewUrl("");
    setUploadError("");
    setSelectedImageUrl("");
  };

  const handleConfirmSelection = async () => {
    if (!selectedImageUrl) return;

    // If it's an uploaded file, process it first
    if (uploadedFile && selectedImageUrl === uploadPreviewUrl) {
      setIsProcessing(true);
      setUploadError("");

      try {
        const result = await processPhoto(uploadedFile);
        if (result.success && result.imageUrl) {
          onSelect(result.imageUrl);
          onClose();
        } else {
          setUploadError(result.error || "Failed to process photo");
        }
      } catch (error) {
        setUploadError("Failed to process photo");
      } finally {
        setIsProcessing(false);
      }
    } else {
      // It's a curated image
      onSelect(selectedImageUrl);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] z-[20] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            Choose Activity Photo
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("curated")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "curated"
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <ImageIcon className="w-4 h-4 inline mr-2" />
            Curated Photos
          </button>
          <button
            onClick={() => setActiveTab("upload")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "upload"
                ? "text-primary border-b-2 border-primary bg-primary/5"
                : "text-gray-600 hover:text-gray-800"
            }`}
          >
            <UploadIcon className="w-4 h-4 inline mr-2" />
            Upload Photo
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === "curated" ? (
            // Curated photos tab
            <>
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-3 text-gray-600">
                    Finding relevant photos...
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {imageOptions.map((url, index) => {
                    return (
                      <div
                        key={url + index}
                        className={`relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer transition-all duration-200 ${
                          selectedImageUrl === url
                            ? "ring-3 ring-primary ring-offset-2"
                            : "hover:scale-105 hover:shadow-lg"
                        }`}
                        onClick={() => handleSelectImage(url)}
                      >
                        <img
                          src={url}
                          alt={`Option ${index + 1}`}
                          className="w-full h-full object-cover"
                          onLoad={() => handleImageLoad(url)}
                          onError={handleImageError}
                        />

                        {/* Loading overlay */}
                        {!loadedImages.has(url) && (
                          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}

                        {/* Selection indicator */}
                        {selectedImageUrl === url && (
                          <div className="absolute inset-0 bg-primary bg-opacity-20 flex items-center justify-center">
                            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                              <XIcon className="w-5 h-5 text-white rotate-45" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-sm text-gray-500 text-center mt-4">
                Select a photo that best represents your activity
              </p>
            </>
          ) : (
            // Upload tab
            <>
              {uploadedFile && uploadPreviewUrl ? (
                // Show uploaded image preview
                <div className="space-y-4">
                  <div className="relative">
                    <div className="aspect-[3/4] max-w-xs mx-auto rounded-xl overflow-hidden border-2 border-dashed border-primary">
                      <img
                        src={uploadPreviewUrl}
                        alt="Uploaded preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={handleRemoveUpload}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {uploadError && (
                    <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                      {uploadError}
                    </div>
                  )}

                  <p className="text-sm text-gray-500 text-center">
                    Ready to use your photo
                  </p>
                </div>
              ) : (
                // Upload area
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    <CameraIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Upload Your Photo
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Choose a photo from your device or camera
                    </p>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => setShowEnhancedModal(true)}
                        className="inline-flex items-center justify-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                      >
                        <CameraIcon className="w-4 h-4 mr-2" />
                        Camera & Gallery
                      </button>
                    </div>
                  </div>

                  <div className="text-xs text-gray-400 text-center space-y-1">
                    <p>Supported formats: JPEG, PNG, WebP</p>
                    <p>Maximum size: 10MB</p>
                  </div>

                  {uploadError && (
                    <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
                      {uploadError}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={!selectedImageUrl || isLoading || isProcessing}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                "Use This Photo"
              )}
            </button>
          </div>
        </div>
      </div>

      {showEnhancedModal && (
        <EnhancedPhotoSelectionModal
          isOpen={showEnhancedModal}
          onClose={() => setShowEnhancedModal(false)}
          onSelect={handleEnhancedPhotoSelect}
          title="Select Activity Photo"
          allowCamera={true}
          allowGallery={true}
          photoOptions={{
            quality: 85,
            width: 800,
            height: 600,
          }}
        />
      )}
    </div>
  );
};

export default ActivityImageSelectionModal;
